"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Mic, Square, Pause, Play, AlertCircle } from "lucide-react";

interface AudioRecorderProps {
  onRecordingComplete: (blob: Blob, fileName: string) => void;
}

// 録音状態の型定義
type RecordingState = 'idle' | 'recording' | 'paused' | 'stopped';

const AudioRecorder: React.FC<AudioRecorderProps> = ({ onRecordingComplete }) => {
  // 状態管理
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  
  // MediaRecorderとストリームの参照
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  // 録音時間のフォーマット
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // タイマーの開始
  const startTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      setRecordingTime((prevTime) => prevTime + 1);
    }, 1000);
  }, []);
  
  // タイマーの停止
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  
  // メディアレコーダーの設定
  const setupMediaRecorder = useCallback(async () => {
    try {
      setPermissionError(null);
      
      // ユーザーのマイク入力を要求
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // MediaRecorderの作成
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm' // 多くのブラウザで対応している形式
      });
      
      // データが利用可能になった時のイベントハンドラー
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      // 録音が停止した時のイベントハンドラー
      mediaRecorder.onstop = () => {
        // Blobの作成
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        
        // 再生用URLの作成
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        
        // ストリームの停止
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        
        // タイマーの停止
        stopTimer();
        setRecordingState('stopped');
        
        // 自動的に録音データを使用
        const timestamp = new Date().toISOString().replace(/[-:.]/g, '').substring(0, 15);
        const fileName = `recording_${timestamp}.webm`;
        onRecordingComplete(blob, fileName);
      };
      
      mediaRecorderRef.current = mediaRecorder;
    } catch (error) {
      console.error('マイクへのアクセスエラー:', error);
      setPermissionError('マイクへのアクセスが許可されていません。設定を確認してください。');
    }
  }, [stopTimer, onRecordingComplete]);
  
  // 録音開始
  const startRecording = useCallback(async () => {
    // 既存の録音をクリア
    chunksRef.current = [];
    setAudioUrl(null);
    setRecordedBlob(null);
    setRecordingTime(0);
    
    // MediaRecorderがなければ設定
    if (!mediaRecorderRef.current) {
      await setupMediaRecorder();
    }
    
    // 録音開始
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.start(10); // 10ミリ秒ごとにデータを取得
      setRecordingState('recording');
      startTimer();
    }
  }, [setupMediaRecorder, startTimer]);
  
  // 録音一時停止
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState === 'recording') {
      mediaRecorderRef.current.pause();
      setRecordingState('paused');
      stopTimer();
    }
  }, [recordingState, stopTimer]);
  
  // 録音再開
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState === 'paused') {
      mediaRecorderRef.current.resume();
      setRecordingState('recording');
      startTimer();
    }
  }, [recordingState, startTimer]);
  
  // 録音停止
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && (recordingState === 'recording' || recordingState === 'paused')) {
      mediaRecorderRef.current.stop();
      stopTimer();
    }
  }, [recordingState, stopTimer]);
  
  // 録音をキャンセルして最初からやり直し
  const resetRecording = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    stopTimer();
    setRecordingState('idle');
    setRecordingTime(0);
    setAudioUrl(null);
    setRecordedBlob(null);
    chunksRef.current = [];
  }, [stopTimer]);
  
  // クリーンアップ
  useEffect(() => {
    return () => {
      // コンポーネントのアンマウント時にリソースを解放
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // AudioURLの解放
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);
  
  return (
    <div 
      className="upload-area expanded-drop-area px-4 pt-5 pb-7 rounded-lg transition-all border-dashed border-2 border-gray-300 hover:border-primary/50 cursor-pointer"
      role="button"
      tabIndex={0}
    >
      {/* 固定高さのコンテナ - FileDropAreaと合わせる */}
      <div className="flex flex-col items-center justify-center min-h-[140px]">
        <div className="space-y-2 text-center w-1/2 mx-auto">
          {permissionError ? (
            <div className="text-center space-y-4" onClick={(e) => e.stopPropagation()}>
              <AlertCircle className="h-14 w-14 text-gray-500 mx-auto" />
              <div className="text-gray-600 font-medium">{permissionError}</div>
              <button
                type="button"
                onClick={setupMediaRecorder}
                className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors"
                aria-label="再試行"
              >
                再試行
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              {/* 録音時間表示 */}
              <div className="text-3xl font-mono text-gray-700 h-10 flex items-center">
                {formatTime(recordingTime)}
              </div>
              
              {/* 状態表示 */}
              <div className="h-5 flex items-center justify-center">
                {recordingState === 'recording' && (
                  <div className="text-xs text-gray-600 font-normal">録音中...</div>
                )}
                {recordingState === 'paused' && (
                  <div className="text-xs text-gray-600 font-normal">一時停止中</div>
                )}
              </div>
              
              {/* 録音コントロール */}
              <div className="flex justify-center gap-3 mt-3 h-16 items-center" onClick={(e) => e.stopPropagation()}>
                {recordingState === 'idle' && (
                  <button
                    type="button"
                    onClick={startRecording}
                    className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-800 hover:bg-gray-700 text-white transition-all"
                    title="録音開始"
                    aria-label="録音開始"
                  >
                    <Mic size={24} />
                  </button>
                )}
                
                {recordingState === 'recording' && (
                  <>
                    <button
                      type="button"
                      onClick={pauseRecording}
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-600 hover:bg-gray-500 text-white transition-all"
                      title="一時停止"
                      aria-label="一時停止"
                    >
                      <Pause size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={stopRecording}
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-all"
                      title="録音停止"
                      aria-label="録音停止"
                    >
                      <Square size={16} />
                    </button>
                  </>
                )}
                
                {recordingState === 'paused' && (
                  <>
                    <button
                      type="button"
                      onClick={resumeRecording}
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-all"
                      title="録音再開"
                      aria-label="録音再開"
                    >
                      <Play size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={stopRecording}
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-600 hover:bg-gray-500 text-white transition-all"
                      title="録音停止"
                      aria-label="録音停止"
                    >
                      <Square size={16} />
                    </button>
                  </>
                )}
                
                {recordingState === 'stopped' && recordedBlob && (
                  <button
                    type="button"
                    onClick={resetRecording}
                    className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-500 hover:bg-gray-400 text-white transition-all"
                    title="最初からやり直し"
                    aria-label="最初からやり直し"
                  >
                    <Mic size={18} />
                  </button>
                )}
              </div>
              
              {/* 録音済みの音声プレイヤー */}
              {audioUrl && recordingState === 'stopped' && (
                <div className="mt-2 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                  <audio src={audioUrl} controls className="w-full" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioRecorder;